interface DiscourseUser {
  id: number;
  username: string;
  name: string;
  avatar_template: string;
  admin: boolean;
  moderator: boolean;
  staff: boolean;
  trust_level: number;
  created_at?: string;
  last_seen_at?: string;
  last_posted_at?: string;
  // These fields might not be available in the current API response
  post_count?: number;
  posts_read?: number;
  time_read?: number;
}

interface DiscourseActivity {
  id: number;
  title: string;
  excerpt: string;
  created_at: string;
  category: string;
  post_number: number;
  topic_id: number;
  action_type: number;
}

interface ActivityResponse {
  activities: DiscourseActivity[];
  meta: {
    username: string;
    count: number;
    filter_applied: number;
    timestamp: string;
  };
}

class DiscourseService {
  private backendUrl =
    process.env.REACT_APP_API_BASE_URL;

  async getCurrentUser(): Promise<DiscourseUser | null> {
    try {
      const response = await fetch(
        `${this.backendUrl}/api/discourse/current-user`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(10000), // 10 seconds
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Backend API error: ${response.status} - ${
            errorData.message || "Unknown error"
          }`
        );
      }

      const data = await response.json();
      return data.user || null;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.error("Request timeout: Failed to fetch Discourse user");
      } else {
        console.error(
          "Failed to fetch Discourse user:",
          error instanceof Error ? error.message : String(error)
        );
      }
      return null;
    }
  }

  async getUserActivity(username: string): Promise<DiscourseActivity[]> {
    try {
      // Input validation
      if (!username || typeof username !== "string") {
        throw new Error("Username is required and must be a string");
      }

      const response = await fetch(
        `${this.backendUrl}/api/discourse/user-activity/${encodeURIComponent(
          username
        )}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          signal: AbortSignal.timeout(15000), // 15 seconds for activities
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Backend API error: ${response.status} - ${
            errorData.message || "Unknown error"
          }`
        );
      }

      const data: ActivityResponse = await response.json();

      // Log activity info in development
      if (process.env.NODE_ENV === "development") {
        console.log(`📊 Activities loaded for ${username}:`, {
          count: data.activities?.length || 0,
          meta: data.meta,
        });
      }

      return data.activities || [];
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.error(
          `Request timeout: Failed to fetch activities for ${username}`
        );
      } else {
        console.error(
          `Failed to fetch Discourse activity for ${username}:`,
          error instanceof Error ? error.message : String(error)
        );
      }
      return [];
    }
  }

  async getUserStats(username: string): Promise<any> {
    try {
      const response = await fetch(
        `${this.backendUrl}/api/discourse/user-stats/${encodeURIComponent(
          username
        )}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          signal: AbortSignal.timeout(10000),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user stats: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to fetch user stats for ${username}:`, error);
      return null;
    }
  }

  // Health check method for monitoring
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.backendUrl}/api/discourse/health`, {
        method: "GET",
        signal: AbortSignal.timeout(5000), // 5 seconds
      });

      return response.ok;
    } catch (error) {
      console.error(
        "Discourse service health check failed:",
        error instanceof Error ? error.message : String(error)
      );
      return false;
    }
  }
}

export const discourseService = new DiscourseService();
export type { DiscourseUser, DiscourseActivity, ActivityResponse };
